import React, { useRef, useEffect } from 'react';
import { FLOOR_LIST } from '../../config';

const PlaceholderDisplay = ({
  id,
  className = '',
  content = null,
  onCancel,
  floors = []
}) => {
  const placeholderRef = useRef(null);
  const shapeRef = useRef(null);

  useEffect(() => {
    updateDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const updateDisplay = () => {
    const placeholder = placeholderRef.current;
    const shape = shapeRef.current;

    if (!placeholder || !shape) return;

    if (content && content.floor && content.floor !== '-') {
      placeholder.style.display = 'flex';

      // クラスの適切な設定
      const targetClass = content.label === '現在' ? 'ux-bg-selected-now' : 'ux-bg-selected-go';
      const otherClass = content.label === '現在' ? 'ux-bg-selected-go' : 'ux-bg-selected-now';

      // 既に正しいクラスが設定されている場合はスキップ
      if (!shape.classList.contains(targetClass)) {
        shape.classList.remove(otherClass);
        shape.classList.add(targetClass);
      }

      // 端階の判定と二重線クラスの追加
      const floorIndex = floors.findIndex(f => f.floor === content.floor);
      const isFirstFloor = floorIndex === floors.length - 1; // 逆順なので最後が最初の階
      const isLastFloor = floorIndex === 0; // 逆順なので最初が最後の階

      // 既存の二重線クラスを削除
      shape.classList.remove('border-t-double', 'border-b-double');

      // 端階の場合は二重線クラスを追加
      if (isFirstFloor) {
        shape.classList.add('border-t-double');
      }
      if (isLastFloor) {
        shape.classList.add('border-b-double');
      }

      // スタイルを強制的に適用
      placeholder.style.visibility = 'visible';
      placeholder.style.opacity = '1';
      placeholder.style.position = 'relative';

      // コンテンツを生成
      renderPlaceholderContent(shape, content);

      placeholder.dataset.floor = content.floor;

    } else {
      // コンテンツがない、または階が'-'の場合は即座に非表示
      hidePlaceholder(placeholder, shape);
    }
  };

  // プレースホルダーを即座に非表示にする関数
  const hidePlaceholder = (placeholder, shape) => {
    if (placeholder) {
      placeholder.style.display = 'none';
      placeholder.style.visibility = 'hidden';
      placeholder.style.opacity = '0';
      placeholder.classList.remove('ux-bg-selected-now', 'ux-bg-selected-go');
      // dataset.floorは削除しない（キャンセル処理で使用される）
      // placeholder.dataset.floor = '';
    }

    if (shape) {
      shape.innerHTML = '';
      shape.onclick = null;
      shape.style.cursor = 'default';
      // 背景色クラスと二重線クラスを削除
      shape.classList.remove('ux-bg-selected-now', 'ux-bg-selected-go', 'border-t-double', 'border-b-double');
    }
  };

  const renderPlaceholderContent = (shape, content) => {
    if (!content || !content.floor || content.floor === '-') return;

    shape.innerHTML = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-center justify-between w-full px-6 mx-1';

    // 左側のスペーサー（ラベルと同じ幅を確保）
    const leftSpacer = document.createElement('div');
    leftSpacer.className = 'flex-shrink-0 opacity-0';

    const spacerLabel = document.createElement('span');
    spacerLabel.className = 'text-sm font-bold';
    spacerLabel.textContent = content.label;
    leftSpacer.appendChild(spacerLabel);

    // 中央の階画像
    const centerContainer = document.createElement('div');
    centerContainer.className = 'flex-1 flex justify-center';

    // 内部値(floor)から表示値(frontName)を取得
    const floorData = floors.find(f => f.floor === content.floor);
    const displayValue = floorData?.frontName || content.floor;

    // 階画像を手動で作成
    const floorImageDiv = document.createElement('div');
    floorImageDiv.className = 'flex justify-center items-center space-x-1';

    displayValue.split('').forEach(char => {
      const img = document.createElement('img');
      img.src = `./assets/static/CF-W__/CF-W_${char}.png`;
      img.className = `${FLOOR_LIST.IMAGE_HEIGHT} w-auto`;
      img.alt = char;
      img.onerror = function () {
        const span = document.createElement('span');
        span.textContent = char;
        span.className = 'text-3xl font-light text-gray-700';
        floorImageDiv.replaceChild(span, img);
      };
      floorImageDiv.appendChild(img);
    });

    centerContainer.appendChild(floorImageDiv);

    // 右側のラベル
    const rightContainer = document.createElement('div');
    rightContainer.className = 'flex-shrink-0 flex justify-end';

    const label = document.createElement('span');
    label.className = 'text-sm ux-text-lightgray font-bold';
    label.textContent = content.label;
    rightContainer.appendChild(label);

    wrapper.appendChild(leftSpacer);
    wrapper.appendChild(centerContainer);
    wrapper.appendChild(rightContainer);
    shape.appendChild(wrapper);

    // クリックイベント（キャンセル処理を先に呼ぶ）
    shape.style.cursor = 'pointer';
    shape.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // キャンセル処理を実行（親の状態を更新）
      if (onCancel) {
        onCancel(content.label, content.floor);
      }

      // 親の状態更新により、contentプロップが変わって
      // useEffect → updateDisplay() → hidePlaceholder()が自動的に呼ばれる
    };
  };

  return (
    <div
      ref={placeholderRef}
      id={id}
      className={`
        placeholder-wrapper 
        h-[calc(100svh/72*7)]
        box-border 
        hidden 
        cursor-pointer 
        z-10 
        w-full
        transition-all 
        duration-200 
        ease-in-out
        ${className}
      `}
      style={{ display: 'none' }}
    >
      <div
        ref={shapeRef}
        className="
          placeholder-shape 
          w-full
          h-[calc(100svh/72*7)]
          flex 
          items-center 
          justify-center 
          text-white 
          font-semibold
          rounded
          transition-all 
          duration-200 
          ease-in-out
          mx-[calc(100vw/36)]
        "
      />
    </div>
  );
};

export default PlaceholderDisplay;